import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { NewsCard } from "./news-card.component";
import "../../scss/news-styling-component/news-grid.style.scss";
import { useTranslation } from "react-i18next";
import { NewsPagination } from "./news-pagination.component";
import { fetchPaginatedNews } from "../../features/news/news.slice";
import { RootState, AppDispatch } from "../../store";
import { ImgFile } from "../images/png/icons";
import { SkeletonCard } from "./news-skeleton-card.component";
import { formatDate } from "../../helper/formatdate.helper"
import { getCategoryName } from "../../helper/news.helper";
interface NewsGridProps {
  searchResults?: any;
  searchQuery?: string;
  onSearchPage?: (page: number) => void;
}

export const NewsGrid: React.FC<NewsGridProps> = ({
  searchResults,
  searchQuery,
  onSearchPage
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const { paginatedNews, loading, error } = useSelector((state: RootState) => state.news);

  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState(1);
  const itemsPerPage = 9;
  useEffect(() => {
    if (searchQuery && searchQuery.trim() !== '') {
    } else {
      setCurrentPage(1);
    }
  }, [searchQuery]);
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory]);
  interface NewsItem {
    heading: string;
    fullContent: string;
    shortContent: string;
    slug: string;
    thumbnailUrl: string | null;
    newCategory: number;
    isActive: boolean;
    estimatedReadTime: number;
    metaTitle: string;
    metaDescription: string;
    metaKeywords: string;
    nametoShow: string;
    createdBy: number;
    updatedBy: number | null;
    createdDate: string;
    updatedDate: string;
  }

  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      const params = {
        pageNumber: currentPage,
        pageSize: itemsPerPage,
        ...(activeCategory !== 0 && { categoryId: activeCategory }),
      };
      console.log('Fetching paginated news with params:', params);
      dispatch(fetchPaginatedNews(params));
    }
  }, [dispatch, currentPage, itemsPerPage, activeCategory, searchQuery]);

  const allNewsItems: NewsItem[] = searchQuery && searchResults
    ? searchResults?.data || searchResults?.response || []
    : paginatedNews?.data || [];

  const newsItems = searchQuery && searchResults
    ? allNewsItems.filter(item => item.isActive)
    : activeCategory === 0
      ? allNewsItems.filter(item => item.isActive)
      : allNewsItems.filter(item => item.newCategory === activeCategory && item.isActive);

  console.log('Filtered news items:', newsItems);

  const totalItems =
    searchQuery && searchResults
      ? searchResults?.totalCount || newsItems.length
      : paginatedNews?.totalCount || 0;
  console.log('Total items:', totalItems, itemsPerPage);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const currentDisplayPage = searchQuery && searchResults
    ? searchResults?.currentPage || 1
    : currentPage;

  const handlePageChange = (page: number) => {
    if (searchQuery && searchResults) {
      onSearchPage?.(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleCategoryChange = (category: number) => {
    setActiveCategory(category);
  };

  const handlePages = (page: number) => {
    setCurrentPage(page);
  }

  if (error) return <div className="text-center p-4 text-danger">Error loading news: {error}</div>;

  return (
    <section className="news-grid">
      <Container>
        {/* Search Results Header */}
        {searchQuery && (
          <div className="search-results-header">
            <h3>{t("SEARCH_RESULTS_FOR", `Search results for "${searchQuery}"`)}</h3>
            <p>
              {totalItems > 0
                ? `${totalItems} ${t("ARTICLES_FOUND", "articles found")}`
                : t("NO_ARTICLES_FOUND", "No articles found")
              }
              {totalItems > itemsPerPage && (
                <span className="text-muted">
                  {" "}({t("SHOWING", "Showing")} {(currentDisplayPage - 1) * itemsPerPage + 1}-{Math.min(currentDisplayPage * itemsPerPage, totalItems)} {t("OF", "of")} {totalItems})
                </span>
              )}
            </p>
          </div>
        )}

        {/* Category Filter - Hide during search */}
        {!searchQuery && (
          <div className="news-grid-header">
            {[
              { id: 1, label: t("COMPANY_NEWS") },
              { id: 2, label: t("PRESS_RELEASES") },
              { id: 3, label: t("GUIDES") },
            ].map(({ id, label }) => (
              <button
                key={id}
                className={`news-category-btn${activeCategory === id ? " active" : ""}`}
                onClick={() => handleCategoryChange(id)}
                aria-current={activeCategory === id ? "page" : undefined}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <Row className={`news-grid-row ${newsItems.length === 1 ? 'justify-content-center' : ''}`}>
          {(loading || (searchQuery && !searchResults)) ? (
            Array.from({ length: searchQuery ? 6 : itemsPerPage }, (_, index) => (
              <Col
                key={`skeleton-${index}`}
                xs={12}
                md={6}
                lg={4}
                className="news-grid-col"
              >
                <SkeletonCard />
              </Col>
            ))
          ) : newsItems && newsItems.length > 0 ? (
            newsItems.map((item: NewsItem) => (
              <Col
                key={item.slug}
                xs={12}
                md={6}
                lg={4}
                className="news-grid-col"
              >
                <NewsCard
                  image={item.thumbnailUrl || ImgFile}
                  title={item.heading}
                  date={formatDate(item.createdDate)}
                  description={item.shortContent}
                  tag={getCategoryName(item.newCategory)}
                  link={`/news/${item.slug}?lang=${localStorage.getItem("i18nextLng") || "sv-se"}`}
                  readTime={`${item.estimatedReadTime || 2} minutes`}
                  pressRelease={item.newCategory === 2}
                />
              </Col>
            ))
          ) : (
            <Col xs={12} className="text-center p-4">
              <p>
                {searchQuery
                  ? t("NO_SEARCH_RESULTS", `No articles found for "${searchQuery}". Try different keywords.`)
                  : t("NO_NEWS_AVAILABLE", "No news articles available at the moment.")
                }
              </p>
            </Col>
          )}
        </Row>

        {/* Pagination and Loading */}
        {!loading && totalPages > 1 && (
          <NewsPagination
            currentPage={currentPage}
            handlePages={handlePages}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </Container>
    </section>
  );
};
